import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, RefreshCw, Loader2, Database, Search, CheckCircle2, XCircle, Copy, Clock } from "lucide-react";
import { getDiscoveryStats, getDiscoverySources, getDiscoveryRuns, runDiscovery } from "@/lib/apiClient";
import AdminNav from "@/components/AdminNav";
import { toast } from "sonner";

function Stat({ label, value, accent = "bg-white" }) {
  return (
    <div className={`border-2 border-ink p-4 ${accent}`}>
      <div className="font-display font-extrabold text-3xl tracking-tight leading-none">{value ?? "—"}</div>
      <div className="eyebrow text-ink/60 mt-2">{label}</div>
    </div>
  );
}

export default function AdminDiscovery() {
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [batches, setBatches] = useState(3);
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => {
    getDiscoveryStats().then(setStats);
    getDiscoverySources().then(setSources);
    getDiscoveryRuns().then(setRuns);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await runDiscovery({ batches: Number(batches), num_queries: 6, analyze_limit: 10, label: "manual" });
      toast[res.started ? "success" : "message"](res.started ? `Discovery started (${res.batches} batches)` : res.message);
      setTimeout(load, 1500);
    } catch (e) { toast.error("Failed to start discovery"); }
    finally { setStarting(false); }
  };

  const c = stats?.counts || {};

  return (
    <div>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-ink/60">Behind the scenes</p>
            <h1 className="font-display font-extrabold text-4xl display-tight uppercase">Discovery Engine</h1>
            <p className="text-sm text-ink/60 mt-1">
              Internet discovery → candidate → AI qualification → dedup → human approval → publish. Nothing auto-publishes.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="eyebrow text-ink/60 block mb-1">Batches</label>
              <input type="number" min={1} max={40} value={batches} onChange={(e) => setBatches(e.target.value)}
                className="w-20 h-11 px-3 border-2 border-ink outline-none" data-testid="discovery-batches-input" />
            </div>
            <button onClick={start} disabled={starting || stats?.running} data-testid="run-discovery-button"
              className="btn-tactile inline-flex items-center gap-2 bg-ink text-white font-bold px-5 h-11 hover:bg-cobalt disabled:opacity-60">
              {starting || stats?.running ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}
              {stats?.running ? "Running…" : "Run Discovery"}
            </button>
            <button onClick={load} className="btn-tactile h-11 w-11 flex items-center justify-center border-2 border-ink hover:bg-secondary"><RefreshCw size={16} /></button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-ink/60">
          <span className="inline-flex items-center gap-1.5"><Clock size={14} /> Last run: <strong className="text-ink">{stats?.last_run ? new Date(stats.last_run).toLocaleString() : "never"}</strong></span>
          <span className="inline-flex items-center gap-1.5"><RefreshCw size={14} /> Next run: <strong className="text-ink">{stats?.next_run || "Daily 02:00 UTC"}</strong></span>
          <span>Est. Tavily credits used: <strong className="text-ink">{stats?.total_credits_estimate ?? 0}</strong></span>
          {stats?.running && <span className="inline-flex items-center gap-1.5 text-cobalt font-semibold"><Loader2 size={14} className="animate-spin" /> discovery in progress</span>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-6">
          <Stat label="URLs discovered" value={c.urls_discovered} />
          <Stat label="Analyzed" value={c.analyzed} />
          <Stat label="Qualified" value={c.qualified} accent="bg-brlime" />
          <Stat label="Possible" value={c.possible} accent="bg-bryellow" />
          <Stat label="Rejected" value={c.rejected} />
          <Stat label="Duplicates" value={c.duplicates} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <Stat label="Ready for review" value={c.awaiting_review} accent="bg-cobalt text-white" />
          <Stat label="Published this week" value={c.published_this_week} accent="bg-brlime" />
          <Stat label="Approved → published" value={c.candidates_approved} />
          <Stat label="Sources checked" value={c.sources_checked} />
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-display font-extrabold text-2xl uppercase">Source Coverage</h2>
          <Link to="/admin/discovery/queue" className="group inline-flex items-center gap-1 font-semibold text-sm text-cobalt">Review candidates →</Link>
        </div>
        <div className="mt-3 border-2 border-ink overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="source-coverage-table">
            <thead><tr className="bg-ink text-white text-left">
              <th className="px-4 py-3">Source</th><th className="px-4 py-3">Pages checked</th>
              <th className="px-4 py-3">Candidates</th><th className="px-4 py-3">Qualified</th>
              <th className="px-4 py-3">Duplicates</th><th className="px-4 py-3">Last checked</th>
            </tr></thead>
            <tbody>
              {sources.filter((s) => s.pages_checked > 0 || s.candidates > 0).map((s) => (
                <tr key={s.name} className="border-t border-ink/10">
                  <td className="px-4 py-2.5 font-semibold">{s.name}</td>
                  <td className="px-4 py-2.5">{s.pages_checked}</td>
                  <td className="px-4 py-2.5">{s.candidates}</td>
                  <td className="px-4 py-2.5">{s.qualified}</td>
                  <td className="px-4 py-2.5">{s.duplicates}</td>
                  <td className="px-4 py-2.5 text-ink/50">{s.last_checked ? new Date(s.last_checked).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {sources.filter((s) => s.pages_checked > 0 || s.candidates > 0).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-ink/40">No sources checked yet. Run discovery to begin.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <h2 className="font-display font-extrabold text-2xl uppercase mt-8">Recent Runs</h2>
        <div className="mt-3 border-2 border-ink overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="runs-table">
            <thead><tr className="bg-ink text-white text-left">
              <th className="px-4 py-3">Started</th><th className="px-4 py-3">Label</th><th className="px-4 py-3">Queries</th>
              <th className="px-4 py-3">URLs</th><th className="px-4 py-3">Analyzed</th><th className="px-4 py-3">Qualified</th>
              <th className="px-4 py-3">Possible</th><th className="px-4 py-3">Rejected</th><th className="px-4 py-3">Dupes</th><th className="px-4 py-3">Credits</th>
            </tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-ink/10">
                  <td className="px-4 py-2.5 text-ink/60 whitespace-nowrap">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="px-4 py-2.5">{r.label}</td>
                  <td className="px-4 py-2.5">{r.queries_run}</td><td className="px-4 py-2.5">{r.urls_found}</td>
                  <td className="px-4 py-2.5">{r.analyzed}</td>
                  <td className="px-4 py-2.5 font-semibold">{r.qualified}</td><td className="px-4 py-2.5">{r.possible}</td>
                  <td className="px-4 py-2.5">{r.rejected}</td><td className="px-4 py-2.5">{r.duplicates}</td>
                  <td className="px-4 py-2.5 text-ink/50">{r.credits_estimate}</td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-ink/40">No runs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
